import React, { useState, useMemo, useEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import { useSingleEffect } from 'react-haiku';
import { useCollect } from 'hooks';

import Show from 'components/Show';
import Tabs from 'components/Misc/Tabs';

import Information from 'containers/Me/Widgets/Information';
import Contact from 'containers/Me/Widgets/Contact';

import General from 'containers/Me/General';
import Address from 'containers/Me/Address';
import Password from 'containers/Me/Password';
import Devices from 'containers/Me/Devices';

import { useAppStore } from 'store/useAppStore';
import { useMeProfileStore } from 'store/me/useMeProfileStore';

const Me = () => {
	// Use app's store
	const [id, setSection, setTitle]  = useAppStore(state => [state.user.id, state.setSection, state.setTitle]);

	// Set section on mount
	useSingleEffect(() => setSection('/me'));

	// Set title on mount
	useSingleEffect(() => setTitle('Iestatījumi'));

	// Store active profile tab
	const [tab, setTab] = useState(null);

	// Use user's profile store
	const [user, onInit, onReset] = useMeProfileStore(state => [
		state.user,
		state.onInit,
		state.onReset
	]);

	// Store request parameters
	const parameters = useMemo(() => ({
		eager: 'address'
	}), []);

	// Store profile tabs
	const tabs = useMemo(() => {
		switch (user.role) {
		case 'adminstrator':
		case 'employee':
			return [
				{ url: '/me', text: 'Pamatinformācija', name: '' },
				{ url: '/me/address', text: 'Adrese', name: 'address' },
				{ url: '/me/authorization', text: 'Autorizācija', name: 'authorization' },
				{ url: '/me/devices', text: 'Ierīces', name: 'devices' },
			];
		default:
			return [
				{ url: '/me', text: 'Pamatinformācija', name: '' },
				{ url: '/me/address', text: 'Adrese', name: 'address' },
				{ url: '/me/authorization', text: 'Autorizācija', name: 'authorization' }
			];
		}
	}, [user.role]);

	// Store requested user
	const [[result, progress], collect] = useCollect(`users/${id}`, parameters);

	// Collect user on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	// Initialize user's data after collection
	useEffect(() => {
		if (result) onInit(result);

		// Reset user's data on unmount
		return () => onReset();
	}, [result]);

	return (
		<div className="flex flex-col gap-4 xl:flex-row xl:items-start">
			<div className="grid grid-cols-1 gap-4 w-full sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-1 xl:max-w-xs">
				{/* User's information */}
				<Information />

				{/* User's contact information */}
				<Contact progress={progress} />
			</div>

			<div className="flex-1">
				{/* Profile tabs */}
				<Tabs
					tabs={tabs}
					selected={tab}
				/>

				<Switch>
					{/* User's general information edit page */}
					<Route exact path="/me">
						<General tab="" setTab={setTab} />
					</Route>

					{/* User's address information edit page */}
					<Route exact path="/me/address">
						<Address tab="address" setTab={setTab} />
					</Route>

					{/* User's address information edit page */}
					<Route exact path="/me/authorization">
						<Password tab="authorization" setTab={setTab} />
					</Route>

					{/* User's devices information edit page */}
					<Show if={['adminstrator', 'employee'].includes(user?.role)}>
						<Route exact path="/me/devices">
							<Devices tab="devices" setTab={setTab} />
						</Route>
					</Show>
				</Switch>
			</div>
		</div>
	);
};

export default Me;
